import { generatePath } from '@toggle/helpers';
import { z } from 'zod';

import { timeSeriesDataSchema } from './timeseries-schema';

const themeOverviewResponseSchema = z.object({
  name: z.string(),
  description: z.string(),
  logo_url: z.string().optional(),
});

export type ThemeOverviewResponse = z.infer<typeof themeOverviewResponseSchema>;

export const themePerformanceSchema = z.object({
  index: z.string().datetime(),
  value: z.number(),
});

const entitiesSchema = z.record(
  z.string(),
  z.array(themePerformanceSchema).nullable()
);

export const calculatorResponseSchema = z.object({
  entities: entitiesSchema,
  aggregate: z.array(themePerformanceSchema),
});

export type CalculatorResponse = z.infer<typeof calculatorResponseSchema>;

const performanceReturnsSchema = z.object({
  '1d': z.number(),
  '1w': z.number(),
  '1m': z.number(),
  '1y': z.number(),
});

const getThemePerformanceSchema = z.object({
  data: z.array(
    z.object({
      timestamp: z.iso.datetime(),
      value: z.number(),
    })
  ),
  entities: z.array(z.string()).optional(),
  performance: performanceReturnsSchema.extend({
    '3m': z.number(),
    '6m': z.number(),
  }),
});
export type GetThemePerformance = z.infer<typeof getThemePerformanceSchema>;

export const performanceResponseSchema = z.object({
  themes: z.array(
    performanceReturnsSchema.extend({
      id: z.string(),
    })
  ),
  page: z.number(),
  size: z.number(),
});

export type PerformanceResponse = z.infer<typeof performanceResponseSchema>;

const countrySchema = z.object({
  code: z.string(),
  name: z.string(),
  evidence: z.string(),
});

export const countryExposureSchema = z.object({
  result: z.array(countrySchema),
});

export type CountryExposureResponse = z.infer<typeof countryExposureSchema>;

const relationshipSchema = z.object({
  id: z.string(),
  description: z.string(),
  rank: z.number(),
  rank_change: z.number().nullish(),
  evidence: z.array(z.string()).nullish(),
  source_evidence: z.string().nullish(),
});

const parentSchema = z.object({
  id: z.string(),
  name: z.string(),
  logo_url: z.string(),
});

const themeSchema = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string(),
  logo_url: z.string(),
  parent: parentSchema,
});

export const themeDetailsSchema = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string(),
  logo_url: z.string(),
  parent_theme: parentSchema,
});

export type ThemeSchemaResponse = z.infer<typeof themeDetailsSchema>;

export const themeExposureSchema = z.object({
  theme: themeSchema,
  relationship: relationshipSchema,
});

export type ThemeExposure = z.infer<typeof themeExposureSchema>;

export const themeExposureResponseSchema = z.array(themeExposureSchema);

export type ThemeExposureResponse = z.infer<typeof themeExposureResponseSchema>;

const themeCompetitorResponseSchema = z.object({
  company: z.string(),
  theme: themeSchema,
  competitors: z.array(z.string()),
});
export type ThemeCompetitorResponse = z.infer<
  typeof themeCompetitorResponseSchema
>;

export const themeCompanySchema = z.object({
  company: z.string(),
  relationship: relationshipSchema,
});
export type ThemeCompany = z.infer<typeof themeCompanySchema>;

const themeCompaniesResponseSchema = z.array(themeCompanySchema);
export type ThemeCompaniesResponse = z.infer<
  typeof themeCompaniesResponseSchema
>;

export const themeChildSchema = z.object({
  theme: themeSchema,
  company_count: z.number(),
  is_company_exposed: z.boolean(),
});
export type ThemeChild = z.infer<typeof themeChildSchema>;

const themeChildrenResponseSchema = z.array(themeChildSchema);
export type ThemeChildrenResponse = z.infer<typeof themeChildrenResponseSchema>;

const companyCompetitorsWithThemesSchema = z.object({
  company: z.string(),
  relationship: relationshipSchema,
  competing_themes: z.array(themeSchema),
});
const companyCompetitorsWithThemesResponseSchema = z.array(
  companyCompetitorsWithThemesSchema
);
export type CompanyCompetitorsWithThemesResponse = z.infer<
  typeof companyCompetitorsWithThemesResponseSchema
>;

const countryExposureRelationshipSchema = z.object({
  id: z.string(),
  description: z.string(),
  rank: z.number(),
  source_evidence: z.string(),
  evidence: z.array(z.string()).optional(),
});

const countryExposureItemSchema = z.object({
  code: z.string(),
  relationship: countryExposureRelationshipSchema,
});

const companyCountryExposureResponseSchema = z.array(countryExposureItemSchema);

export type CompanyCountryExposureItem = z.infer<
  typeof countryExposureItemSchema
>;
export type CompanyCountryExposureResponse = z.infer<
  typeof companyCountryExposureResponseSchema
>;

const regionExposureRelationshipSchema = z.object({
  id: z.string(),
  description: z.string(),
  rank: z.number(),
  source_evidence: z.string(),
  evidence: z.array(z.string()).optional(),
});

const regionExposureItemSchema = z.object({
  name: z.string(),
  icon_url: z.string().url(),
  relationship: regionExposureRelationshipSchema,
});

const companyRegionExposureResponseSchema = z.array(regionExposureItemSchema);

const brandAndProductItemSchema = z.object({
  product: z.string(),
  relationship: relationshipSchema,
});
export type BrandAndProduct = z.infer<typeof brandAndProductItemSchema>;

const brandsAndProductsResponseSchema = z.array(brandAndProductItemSchema);
export type BrandsAndProductsResponse = z.infer<
  typeof brandsAndProductsResponseSchema
>;

export type CompanyRegionExposureItem = z.infer<
  typeof regionExposureItemSchema
>;
export type CompanyRegionExposureResponse = z.infer<
  typeof companyRegionExposureResponseSchema
>;

const themePerformanceSortHorizonSchema = z.enum([
  'intraday',
  '1d',
  '1w',
  '1m',
  '3m',
  '6m',
  '1y',
]);

export type ThemePerformanceSortHorizon = z.infer<
  typeof themePerformanceSortHorizonSchema
>;

export interface ThemePerformanceParams {
  ids?: string;
  sort_order?: string;
  sort_horizon?: ThemePerformanceSortHorizon;
  page?: number;
  page_size?: number;
  min_assets?: number;
  intraday?: boolean;
}

const v2ThemePerformanceItemSchema = z.object({
  id: z.string(),
  entities: z.array(z.string()),
  performance: z.object({
    '1d': z.number(),
    '1w': z.number(),
    '1m': z.number(),
    '3m': z.number(),
    '6m': z.number(),
    '1y': z.number(),
  }),
  intraday: z
    .object({
      current: z.number(),
      last_updated: z.string(),
      market_status: z.string(),
    })
    .nullish(),
});

const v3ThemePerformanceItemSchema = z.object({
  id: z.string(),
  entities: z.array(z.string()),
  performance: z.object({
    '1d': z.number(),
    '1w': z.number(),
    '1m': z.number(),
    '3m': z.number(),
    '6m': z.number(),
    '1y': z.number(),
    intraday: z.number(),
  }),
  intraday_metadata: z
    .object({
      last_updated: z.string(),
    })
    .nullish(),
  market_metadata: z
    .object({
      market_status: z.string(),
    })
    .nullish(),
});

export type V2ThemePerformanceItem = z.infer<
  typeof v2ThemePerformanceItemSchema
>;

/**
 * @deprecated
 */
const v2ThemePerformanceResponseSchema = z.object({
  themes: z.array(v2ThemePerformanceItemSchema),
  page: z.number(),
});

const v3ThemePerformanceResponseSchema = z.object({
  themes: z.array(v3ThemePerformanceItemSchema),
  page: z.number(),
});

/**
 * @deprecated
 */
export type V2ThemePerformanceResponse = z.infer<
  typeof v2ThemePerformanceResponseSchema
>;

export type V3ThemePerformanceResponse = z.infer<
  typeof v3ThemePerformanceResponseSchema
>;

export const themeOverview = {
  getThemeV2: {
    path: (themeId: string) => `theme/v2/${themeId}`,
    schema: themeDetailsSchema,
  },
  themeCalculator: {
    path: 'calculator/v1/performance',
    schema: calculatorResponseSchema,
  },
  /**
   * @deprecated
   */
  themePerformance: {
    path: 'entity-performance/v1/theme',
    schema: performanceResponseSchema,
  },
  /**
   * @deprecated
   */
  getThemePerformance: {
    path: (themeId: string) => `entity-performance/v1/theme/${themeId}`,
    schema: getThemePerformanceSchema,
  },
  /**
   * @deprecated
   */
  getV2ThemePerformance: {
    path: (params?: ThemePerformanceParams) => {
      const basePath = 'entity-performance/v2/theme';

      if (!params) {
        return basePath;
      }

      const filteredParams: Record<string, string | number> = {};

      if (params.ids) {
        filteredParams.ids = params.ids;
      }

      if (params.sort_order) {
        filteredParams.sort_order = params.sort_order;
      }

      if (params.sort_horizon) {
        filteredParams.sort_horizon = params.sort_horizon;
      }

      if (params.page) {
        filteredParams.page = params.page;
      }

      if (params.page_size) {
        filteredParams.page_size = params.page_size;
      }

      if (params.min_assets) {
        filteredParams.min_assets = params.min_assets;
      }

      if (params.intraday) {
        filteredParams.intraday = params.intraday.toString();
      }

      if (Object.keys(filteredParams).length === 0) {
        return basePath;
      }

      return generatePath({
        basePath,
        params: filteredParams,
      });
    },
    schema: v2ThemePerformanceResponseSchema,
  },
  getV3ThemePerformance: {
    path: (params?: ThemePerformanceParams) => {
      const basePath = 'entity-performance/v3/theme';

      if (!params) {
        return basePath;
      }

      const filteredParams = Object.fromEntries(
        Object.entries(params).filter(([_, value]) => value != null)
      );

      if (Object.keys(filteredParams).length === 0) {
        return basePath;
      }

      return generatePath({
        basePath,
        params: filteredParams,
      });
    },
    schema: v3ThemePerformanceResponseSchema,
  },
  spxBenchmark: {
    path: 'ca/v2/q',
    schema: timeSeriesDataSchema,
  },
  // v3
  getCompanyTheme: {
    path: (entityTag: string, limit?: number) => {
      const limitParam = limit ? `?limit=${limit}` : '';
      return `ontology/v3/company/${entityTag}/theme-exposure${limitParam}`;
    },
    schema: themeExposureResponseSchema,
  },
  getCompanyCompetitors: {
    path: (entityTag: string, themeId: string, limit?: number) => {
      const limitParam = limit ? `?limit=${limit}` : '';
      return `ontology/v3/company/${entityTag}/theme/${themeId}/competitors${limitParam}`;
    },
    schema: themeCompetitorResponseSchema,
  },
  getThemeChildren: {
    path: (themeId: string) => `ontology/v3/theme/${themeId}/children`,
    schema: themeChildrenResponseSchema,
  },
  getThemeCompanies: {
    path: (themeId: string, limit?: number) => {
      const limitParam = limit ? `?limit=${limit}` : '';
      return `ontology/v3/theme/${themeId}/company-exposure${limitParam}`;
    },
    schema: themeCompaniesResponseSchema,
  },
  getCompanyCompetitorsWithThemes: {
    path: (entityTag: string, limit?: number) => {
      const limitParam = limit ? `?limit=${limit}` : '';
      return `ontology/v3/company/${entityTag}/competitors${limitParam}`;
    },
    schema: companyCompetitorsWithThemesResponseSchema,
  },
  getCompanyCountryExposure: {
    path: (entityTag: string) =>
      `ontology/v3/company/${entityTag}/country-exposure`,
    schema: companyCountryExposureResponseSchema,
  },
  getCompanyRegionExposure: {
    path: (entityTag: string) =>
      `ontology/v3/company/${entityTag}/region-exposure`,
    schema: companyRegionExposureResponseSchema,
  },
  getBrandsAndProducts: {
    path: (entityTag: string) => `ontology/v3/company/${entityTag}/products`,
    schema: brandsAndProductsResponseSchema,
  },
};
